/* PreviewRenderer.tsx generated by @compiled/babel-plugin v0.36.1 */
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import "./PreviewRenderer.compiled.css";
import * as React from 'react';
import { ax, ix } from "@compiled/react/runtime";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { useCallback, useEffect, useRef } from 'react';
import { bind } from 'bind-event-listener';
import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { ReactRenderer } from '@atlaskit/renderer';
import { RendererActionsContext } from '@atlaskit/renderer/actions';
import { isPartialCopy, isSelectionWithinElement } from '@atlassian/editor-ai-common/utils/renderer';
import { useAnalyticsFlow } from '../../utils/analytics/analyticsFlowUtils';

// override renderer's syntax highlighting styles so it appears consistent with the editor
var rendererOverrides = null;
export var PreviewRenderer = function PreviewRenderer(_ref) {
  var jsonDocument = _ref.jsonDocument,
    showTelepointer = _ref.showTelepointer,
    providerFactory = _ref.providerFactory;
  var analyticsFlow = useAnalyticsFlow();
  var previewRendererRef = useRef(null);
  var copiedOnceRef = useRef(false);

  // IMPORTANT: the renderer will call providerFactory.destroy() when it unmounts. This is why we need to duplicate
  // the providerFactory and add a heap of subscribers to it. This is to avoid this duplicate provider gettting out of
  // sync with the original providerFactory.
  var dupedProviderFactory = useRef(new ProviderFactory());
  useEffect(function () {
    var mentionHandler = function mentionHandler(name, provider) {
      dupedProviderFactory.current.setProvider(name, provider);
    };
    var emojiHandler = function emojiHandler(name, provider) {
      dupedProviderFactory.current.setProvider(name, provider);
    };
    var mediaHandler = function mediaHandler(name, provider) {
      dupedProviderFactory.current.setProvider(name, provider);
    };
    providerFactory.subscribe('mentionProvider', mentionHandler);
    providerFactory.subscribe('emojiProvider', emojiHandler);
    providerFactory.subscribe('mediaProvider', mediaHandler);
    return function () {
      providerFactory.unsubscribe('mentionProvider', mentionHandler);
      providerFactory.unsubscribe('emojiProvider', emojiHandler);
      providerFactory.unsubscribe('mediaProvider', mediaHandler);
    };
  }, [providerFactory]);

  // Ignored via go/ees005
  // eslint-disable-next-line require-await
  var onCopyHandler = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
    var previewRendererElement;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          previewRendererElement = previewRendererRef.current;
          if (!(!analyticsFlow || !previewRendererElement)) {
            _context.next = 3;
            break;
          }
          return _context.abrupt("return");
        case 3:
          if (isSelectionWithinElement(previewRendererElement)) {
            _context.next = 5;
            break;
          }
          return _context.abrupt("return");
        case 5:
          if (isPartialCopy(previewRendererRef.current)) {
            analyticsFlow.addAttributes({
              contentCopiedPartially: true
            });
          }

          // exit if copied already for this preview
          if (!copiedOnceRef.current) {
            _context.next = 8;
            break;
          }
          return _context.abrupt("return");
        case 8:
          analyticsFlow.addAttributes({
            contentCopied: true
          });
          copiedOnceRef.current = true;
        case 10:
        case "end":
          return _context.stop();
      }
    }, _callee);
  })), [analyticsFlow]);
  useEffect(function () {
    var unbind = bind(document, {
      type: 'copy',
      listener: onCopyHandler
    });
    return unbind;
  }, [onCopyHandler, analyticsFlow]);
  return /*#__PURE__*/React.createElement("div", {
    "data-testid": "preview-renderer",
    ref: previewRendererRef,
    className: ax(["_1gxu4jg8 _1u11i7a9 _1ddw1fxt"])
  }, /*#__PURE__*/React.createElement(RendererActionsContext, null, /*#__PURE__*/React.createElement(ReactRenderer, {
    document: jsonDocument,
    media: {
      // We don't currently support images with captions
      allowLinking: false
    }
    // By default -- the renderer uses a "default schema" which supports all
    // the nodes and marks the markdown transformer can create.
    // see packages/editor/adf-schema/src/schema/default-schema.ts for details.
    ,
    useSpecBasedValidator: true,
    addTelepointer: showTelepointer,
    dataProviders: dupedProviderFactory.current,
    allowPlaceholderText: true
  })));
};